import { DateArg, formatDMY, formatDMYWithTime, parseDate } from '@lbcamden/shared-utils';
import { v5 } from 'uuid';

import { GlobalStore } from '~/api/tinybase/GlobalStore';
import { pcfraActivityDisplayValues } from '~/client/config/display-values/pcfra';

import { bsmNecUsername, homeVisitsNecUsername } from '../config/constants';
import { User } from '../types/app';
import { Note } from '../types/notes';
import { PcfraActivityType } from '../types/pcfra';
import { RpeepStatus, PcfraStatus } from '../types/people';

export type PcfraStatusNoteParams = {
  newPcfraStatus: PcfraStatus;
  prevPcfraStatus?: PcfraStatus;
  newPcfraReviewDate?: string | null;
};

export type RpeepStatusNoteParams = {
  newRpeepStatus: RpeepStatus;
  prevRpeepStatus?: RpeepStatus;
};

export type PcfraActivityNoteParams = {
  activityType: PcfraActivityType;
  activityTimestamp: string;
  newPcfraReviewDate?: string | null;
};

const noteIdNamespace = 'D9E87B29-A337-430C-AEA8-06F474B8781F';

const notePartsDelimiter = '\n\n';

/** Notes in NEC have a composite primary key composed of (entity reusableId, type, created) */
export const deriveNoteId = (
  note: Pick<Note, 'propertyId' | 'entityRef' | 'type'> & { created: DateArg },
) =>
  v5(
    `${note.propertyId}:${note.entityRef}:${note.type}:${parseDate(note.created).toISO()}`,
    noteIdNamespace,
  );

export const getFullNoteText = ({ name, email }: User, note: string) => {
  const author = `*By ${name} (${email})*`;

  return `${author}${notePartsDelimiter}${note}`;
};

const getPcfraReviewDateNoteText = (newPcfraReviewDate?: string | null) => {
  if (newPcfraReviewDate) {
    return `Review date set to: ${formatDMY(newPcfraReviewDate)}`;
  }
  if (newPcfraReviewDate === null) {
    return 'Review date removed.';
  }
};

export const getPcfraStatusNoteText = (
  user: User,
  note: string,
  { newPcfraStatus, prevPcfraStatus, newPcfraReviewDate }: PcfraStatusNoteParams,
) => {
  const pcfraStatusWasActive = prevPcfraStatus === PcfraStatus.Active;

  const statusTextMap: Partial<Record<PcfraStatus, string>> = {
    [PcfraStatus.Necessary]: 'PCFRA to do',
    [PcfraStatus.Unnecessary]: pcfraStatusWasActive ? 'PCFRA revoked' : 'PCFRA not needed',
    [PcfraStatus.Active]: pcfraStatusWasActive ? 'PCFRA suitability reconfirmed' : 'PCFRA active',
  };

  const statusText = statusTextMap[newPcfraStatus];
  const statusTitle = statusText && `**${statusText}**`;
  const noteText = getFullNoteText(user, note);
  const reviewDateText = getPcfraReviewDateNoteText(newPcfraReviewDate);

  return [statusTitle, noteText, reviewDateText].filter(Boolean).join(notePartsDelimiter);
};

export const getRpeepStatusNoteText = (
  user: User,
  note: string,
  { newRpeepStatus, prevRpeepStatus }: RpeepStatusNoteParams,
) => {
  const peepStatusWasActive = prevRpeepStatus === RpeepStatus.Active;

  const statusTextMap: Partial<Record<RpeepStatus, string>> = {
    [RpeepStatus.Active]: peepStatusWasActive ? 'RPEEP updated' : 'RPEEP active',
    [RpeepStatus.Revoked]: 'RPEEP revoked',
  };

  const statusText = statusTextMap[newRpeepStatus];
  const statusTitle = statusText && `**${statusText}**`;
  const noteText = getFullNoteText(user, note);

  return [statusTitle, noteText].filter(Boolean).join(notePartsDelimiter);
};

export const getPcfraActivityNoteText = (
  user: User,
  note: string,
  { activityType, activityTimestamp, newPcfraReviewDate }: PcfraActivityNoteParams,
) => {
  const activityTitle = `**${pcfraActivityDisplayValues[activityType]}**`;
  const timestampText = `*Date and time: ${formatDMYWithTime(activityTimestamp)}*`;
  const noteWithTimestamp = [timestampText, note].join(notePartsDelimiter);
  const noteText = getFullNoteText(user, noteWithTimestamp);
  const reviewDateText = getPcfraReviewDateNoteText(newPcfraReviewDate);

  return [activityTitle, noteText, reviewDateText].filter(Boolean).join(notePartsDelimiter);
};

export const getNoteCreatedByDisplayValue = (createdBy: string) =>
  ({ [homeVisitsNecUsername]: 'Home Visits', [bsmNecUsername]: 'Building Safety Manager' })[
    createdBy
  ] || createdBy;

export const getNoteSubject = (
  { entityRef, type, subtype }: Pick<Note, 'entityRef' | 'type'> & { subtype?: string },
  referenceListDisplayValue: GlobalStore['referenceListDisplayValue'],
  getPersonFullName: (personId: number) => string | undefined,
) => {
  const getTypeDisplayValue = (type: string) =>
    type === 'PRO' ? 'Property' : referenceListDisplayValue('COMBNOTOBJ', type);

  const getSubTypeDisplayValue = (subtype: string) =>
    referenceListDisplayValue('NOTE_SUBTYPE', subtype);

  const isPersonNote = type === 'PAR';
  let firstPart = isPersonNote ? 'Unknown' : getTypeDisplayValue(type);

  if (isPersonNote) {
    firstPart = getPersonFullName(Number(entityRef)) || firstPart;
  }
  const secondPart = subtype && getSubTypeDisplayValue(subtype);

  return `${firstPart}${secondPart ? ` - ${secondPart}` : ''}`;
};
